import { useEffect, useMemo, useState } from 'react';
import { DataError, useList } from '@hana/data';
import { daysBetween, findCarrier } from '@hana/domain';
import { useT } from '@hana/i18n';
import { HeaderTool, Page, PageHeader } from '@hana/shell';
import { Button, Chip, Drawer, Input, Segmented, StateView, Table, useToast, type Column, type ScreenState } from '@hana/ui';
import { IconCalendar, IconChartBar, IconDots, IconFileText, IconPaperclip, IconPencil, IconPlayerPlay, IconPlus, IconRefresh, IconSearch, IconStar, IconX } from '@tabler/icons-react';
import { useP2PAccess } from './access';
import { FeedSourceDrawer } from './FeedSourceDrawer';
import { BOARD_TODAY } from './fixtures';
import { dm, dmHm, dmy, usd, viNum } from './format';
import { FEED_KIND, SOURCE_NAMES, RELEVANT_MIN, effectiveLabel, feedErrors, filterNews, indexStat, routeIndexRows } from './news';
import type { CarrierNotice, FeedSource, MarketIndex, NewsItem } from './types';
import './MarketNewsScreen.i18n';
import s from './news.module.css';

// TODO: "hôm nay" thật khi đổi sang dữ liệu thật.
const TODAY = BOARD_TODAY;
type Tab = 'news' | 'adv' | 'idx' | 'src';

function Spark({ values, label, width = '100%', h = 32, tone }: { values: readonly number[]; label: string; width?: number | string; h?: number; tone?: 'up' | 'down' }) {
  const w = 120;
  const min = Math.min(...values);
  const max = Math.max(...values);
  const pts = values.map((v, i) => `${(i / Math.max(values.length - 1, 1)) * w},${h - 3 - ((v - min) / Math.max(max - min, 1)) * (h - 6)}`).join(' ');
  return (
    <svg viewBox={`0 0 ${w} ${h}`} width={width} height={h} role="img" aria-label={label} className={`${s.spark} ${tone === 'up' ? s.sparkUp : ''}`} preserveAspectRatio="none">
      <polyline points={pts} fill="none" stroke="currentColor" strokeWidth="2" strokeLinejoin="round" strokeLinecap="round" vectorEffect="non-scaling-stroke" />
    </svg>
  );
}

function Reader({ n, onClose }: { n: NewsItem | null; onClose: () => void }) {
  const t = useT();
  const [vi, setVi] = useState(true);
  return (
    <Drawer open={!!n} onOpenChange={(o) => !o && onClose()} title={n ? (vi ? n.titleVi : n.title) : ''} description={n ? `${SOURCE_NAMES[n.source] ?? n.source} · ${dmHm(n.at)}` : undefined}>
      {n && (
        <div className={s.reader}>
          {n.lang === 'en' && (
            <Segmented kind="toggle" label={t('mn.lang')} value={vi ? 'vi' : 'en'} onChange={(k) => setVi(k === 'vi')} items={[{ key: 'vi', label: t('mn.vi') }, { key: 'en', label: 'English' }]} />
          )}
          <section aria-label={t('mn.sum')}>
            <h3>{t('mn.sum')}</h3>
            <p>{n.summary}</p>
          </section>
          {n.carriers.length > 0 && (
            <div className={s.tags}>
              {n.carriers.map((c) => (
                <Chip key={c} label={findCarrier(c)?.name ?? c}>{c}</Chip>
              ))}
            </div>
          )}
          <div className={s.sub}>{t('mn.rel', { n: n.relevance })}</div>
        </div>
      )}
    </Drawer>
  );
}

function NewsTab({ items, read }: { items: readonly NewsItem[]; read: ReadonlySet<string> }) {
  const t = useT();
  const [q, setQ] = useState('');
  const [source, setSource] = useState<string | null>(null);
  const [rel, setRel] = useState(true);
  const [vi, setVi] = useState(true);
  const [hidden, setHidden] = useState<ReadonlySet<string>>(new Set());
  const [open, setOpen] = useState<NewsItem | null>(null);
  const [seen, setSeen] = useState<ReadonlySet<string>>(read);
  const shown = useMemo(() => filterNews(items, { q, source, relevantOnly: rel, hidden }), [items, q, source, rel, hidden]);
  const sources = [...new Set(items.map((i) => i.source))];
  const lowHidden = items.filter((n) => n.relevance < RELEVANT_MIN).length;
  const openNews = (n: NewsItem) => {
    setOpen(n);
    setSeen(new Set(seen).add(n.id));
  };
  return (
    <>
      <div className={s.bar}>
        <div className={s.search}>
          <Input aria-label={t('mn.find')} placeholder={t('mn.findPh')} icon={<IconSearch size={16} aria-hidden="true" />} value={q} onChange={(e) => setQ(e.target.value)} />
        </div>
        <div className={s.chips} role="group" aria-label={t('mn.sources')}>
          <button type="button" className={s.chipBtn} aria-pressed={source === null} onClick={() => setSource(null)}>{t('mn.allSources')} <span className={s.n}>{items.length}</span></button>
          {sources.map((x) => (
            <button key={x} type="button" className={s.chipBtn} aria-pressed={source === x} onClick={() => setSource(source === x ? null : x)}>
              {SOURCE_NAMES[x] ?? x} <span className={s.n}>{items.filter((n) => n.source === x).length}</span>
            </button>
          ))}
        </div>
        <label className={s.tick} title={t('mn.onlyRel', { n: RELEVANT_MIN })}>
          <input type="checkbox" checked={rel} onChange={() => setRel(!rel)} aria-label={t('mn.onlyRel', { n: RELEVANT_MIN })} />
          <IconStar size={14} aria-hidden="true" /> ≥ {RELEVANT_MIN}
        </label>
        <label className={s.tick} title={t('mn.viTitle')}>
          <input type="checkbox" checked={vi} onChange={() => setVi(!vi)} aria-label={t('mn.viTitle')} />
          VI
        </label>
        <span className={s.count}>{t('mn.countN', { a: shown.length, b: items.length })}</span>
      </div>
      {shown.length === 0 ? (
        <StateView state="empty" title={t('mn.noNews')} desc={t('mn.noNewsD')} />
      ) : (
        <section className={s.list} aria-label={t('mn.news')}>
          {shown.map((n) => (
            <article key={n.id} className={s.row}>
              <span className={`${s.dot} ${seen.has(n.id) ? s.read : ''}`} aria-hidden="true" />
              <div className={s.main}>
                <button type="button" className={s.title} onClick={() => openNews(n)}>
                  {vi && n.lang === 'en' ? n.titleVi : n.title}
                </button>
                <div className={s.sum}>{n.summary}</div>
                <div className={s.meta}>
                  <b>{SOURCE_NAMES[n.source] ?? n.source}</b>
                  {n.carriers.slice(0, 3).map((c) => <span key={c} className={s.tag} title={findCarrier(c)?.name ?? c}>{c}</span>)}
                  <span title={dmHm(n.at)}><IconCalendar size={13} aria-hidden="true" /> {dmy(n.at)}</span>
                  <span title={t('mn.rel', { n: n.relevance })}><IconStar size={13} aria-hidden="true" /> {n.relevance} /5</span>
                </div>
              </div>
              <div className={s.acts}>
                <Button size="sm" variant="soft" icon={<IconFileText size={16} aria-hidden="true" />} onClick={() => openNews(n)}>{t('mn.read')}</Button>
                <Button size="sm" variant="ghost" aria-label={t('mn.hideAria', { v: n.titleVi })} title={t('mn.hide')} icon={<IconX size={16} aria-hidden="true" />} onClick={() => setHidden(new Set(hidden).add(n.id))} />
              </div>
            </article>
          ))}
          <div className={s.foot}>{t('mn.newsN', { n: shown.length })}{rel && lowHidden > 0 ? ` · ${t('mn.lowHidden', { a: lowHidden, b: RELEVANT_MIN })}` : ''}</div>
        </section>
      )}
      <Reader n={open} onClose={() => setOpen(null)} />
    </>
  );
}

function NoticeTab({ items }: { items: readonly CarrierNotice[] }) {
  const t = useT();
  const [carrier, setCarrier] = useState<string | null>(null);
  const carriers = [...new Set(items.map((i) => i.carrier))];
  const shown = items.filter((n) => !carrier || n.carrier === carrier).sort((a, b) => a.effective.localeCompare(b.effective));
  const cols: Column<CarrierNotice>[] = [
    { key: 'car', header: t('mn.carrier'), width: 90, cell: (n) => <b title={findCarrier(n.carrier)?.name ?? n.carrier}>{n.carrier}</b> },
    { key: 'kind', header: t('mn.type'), width: 130, cell: (n) => <Chip tone={n.kind === 'Delay' || n.kind === 'Blank sailing' ? 'warn' : 'info'}>{n.kind}</Chip> },
    { key: 'title', header: t('mn.notice'), cell: (n) => (<>{n.titleVi}<div className={s.sub}>{n.title}</div></>) },
    { key: 'eff', header: t('mn.effective'), width: 150, cell: (n) => (<>{dm(n.effective)}/2026<div className={s.sub}>{effectiveLabel(n.effective, TODAY)}</div></>) },
    { key: 'amt', header: t('mn.amount'), width: 190, minor: true, cell: (n) => n.amount || '–' },
    { key: 'ch', header: t('mn.channel'), width: 100, minor: true, cell: (n) => (<span className={s.inline}>{n.channel === 'mail' ? 'email' : 'web'}{n.file && <IconPaperclip size={14} aria-label={n.file} />}</span>) },
  ];
  return (
    <>
      <div className={s.chips} role="group" aria-label={t('mn.carrierFilter')}>
        <button type="button" className={s.chipBtn} aria-pressed={carrier === null} onClick={() => setCarrier(null)}>{t('mn.all')}</button>
        {carriers.map((c) => (
          <button key={c} type="button" className={s.chipBtn} aria-pressed={carrier === c} onClick={() => setCarrier(carrier === c ? null : c)}>{c}</button>
        ))}
      </div>
      <section className={s.card} aria-label={t('mn.carrierNotice')}>
        <Table label={t('mn.carrierNotice')} columns={cols} rows={shown} rowKey={(n) => n.id} dense />
      </section>
    </>
  );
}

function IndexTab({ items }: { items: readonly MarketIndex[] }) {
  const t = useT();
  const [sel, setSel] = useState('');
  const cur = items.find((i) => i.id === sel) ?? items[0];
  const rows = useMemo(() => (cur ? routeIndexRows(cur.values) : []), [cur]);
  return (
    <>
      <section className={s.cards} aria-label={t('mn.indices')}>
        {items.map((i) => {
          const st = indexStat(i);
          return (
            <div key={i.id} className={s.idx}>
              <div className={s.idxHead}><b>{i.id}</b><span className={s.sub}>{i.unit}</span></div>
              <div className={s.idxVal}>
                <b>{usd(st.last)}</b>
                <span className={st.delta > 0 ? s.up : st.delta < 0 ? s.down : s.sub}>{st.delta > 0 ? '▲' : st.delta < 0 ? '▼' : '–'} {Math.abs(st.pct)}%</span>
              </div>
              <Spark values={i.values} label={`${i.id}: ${i.values.join(', ')}`} />
              <span className={s.sub}>{i.source}</span>
            </div>
          );
        })}
      </section>
      {cur && (
        <section className={s.routeBox} aria-label={t('mn.byRoute')}>
          <div className={s.routeHead}>
            <h2>{t('mn.byRouteOf', { v: cur.id })}</h2>
            <Segmented kind="toggle" label={t('mn.indices')} value={cur.id} onChange={setSel} items={items.map((i) => ({ key: i.id, label: i.id }))} />
            <span className={s.sub}>USD / FEU</span>
          </div>
          <div className={`${s.routeRow} ${s.routeTh}`}>
            <span>{t('mn.route')}</span><span className={s.num}>{t('mn.thisPeriod')}</span><span className={s.num}>{t('mn.prevPeriod')}</span><span className={s.num}>{t('mn.change')}</span><span>{t('mn.last8')}</span>
          </div>
          {rows.map((r) => (
            <div key={r.route} className={s.routeRow}>
              <b>{r.route}</b>
              <span className={s.num}>{usd(r.last)}</span>
              <span className={`${s.num} ${s.sub}`}>{usd(r.prev)}</span>
              <span className={`${s.num} ${r.pct > 0 ? s.up : r.pct < 0 ? s.down : s.sub}`}>{r.pct > 0 ? '+' : r.pct < 0 ? '−' : ''}{viNum(Math.abs(r.pct), 1)}%</span>
              <Spark values={r.values} label={r.route} width={120} h={24} tone={r.pct > 0 ? 'up' : undefined} />
            </div>
          ))}
        </section>
      )}
    </>
  );
}

function SourceTab({ items, onEdit }: { items: readonly FeedSource[]; onEdit: (r: FeedSource) => void }) {
  const t = useT();
  const toast = useToast();
  const pending = () => toast({ msg: t('mn.noWrite'), tone: 'err' });
  const cols: Column<FeedSource>[] = [
    { key: 'name', header: t('mn.source'), width: 190, cell: (r) => <b>{r.name}</b> },
    { key: 'kind', header: t('mn.type'), width: 130, minor: true, cell: (r) => (<>{FEED_KIND[r.kind]}{r.tag && <> <b>{r.tag}</b></>}</>) },
    { key: 'url', header: t('mn.detail'), minor: true, cell: (r) => (<span className={s.url}>{r.url || '–'}{r.match && <em> /{r.match}/</em>}</span>) },
    {
      key: 'on',
      header: t('mn.on'),
      width: 80,
      cell: (r) => (
        <button type="button" role="switch" aria-checked={r.enabled} aria-label={t('mn.enable', { v: r.name })} className={`${s.sw} ${r.enabled ? s.swOn : ''}`} onClick={pending}>
          <span />
        </button>
      ),
    },
    {
      key: 'last',
      header: t('mn.lastRun'),
      width: 200,
      cell: (r) => (r.ok === null ? <span className={s.sub} title={r.error}>{t('mn.never')}</span> : r.ok ? <span>{r.lastRun}</span> : <span className={s.down} title={r.error}>{r.lastRun} · {t('mn.err')}</span>),
    },
    { key: 'n', header: t('mn.items7'), width: 100, align: 'right', cell: (r) => r.items7d },
    {
      key: 'act',
      header: '',
      width: 150,
      align: 'right',
      cell: (r) => (
        <span className={s.inline}>
          <Button size="sm" icon={<IconPlayerPlay size={16} aria-hidden="true" />} onClick={pending}>{t('mn.run')}</Button>
          <Button size="sm" icon={<IconPencil size={16} aria-hidden="true" />} onClick={() => onEdit(r)}>{t('mn.edit')}</Button>
        </span>
      ),
    },
  ];
  return (
    <section className={s.card} aria-label={t('mn.feed')}>
      <Table label={t('mn.feed')} columns={cols} rows={items} rowKey={(r) => r.id} dense />
      <div className={s.foot}>{t('mn.feedFoot', { a: items.length, b: feedErrors(items) })}</div>
    </section>
  );
}

export function MarketNewsScreen() {
  const t = useT();
  const access = useP2PAccess();
  const news = useList('newsItem');
  const adv = useList('carrierNotice');
  const idx = useList('marketIndex');
  const src = useList('feedSource');
  const [tab, setTab] = useState<Tab>('news');

  const newsAll = (news.data?.items ?? []) as NewsItem[];
  const advAll = (adv.data?.items ?? []) as CarrierNotice[];
  const idxAll = (idx.data?.items ?? []) as MarketIndex[];
  const srcAll = (src.data?.items ?? []) as FeedSource[];
  // board `advSoon`: hiệu lực trong ≤ 12 ngày, giữ thứ tự thông báo, tối đa 4
  const soon = advAll.filter((n) => { const d = daysBetween(TODAY, n.effective); return d >= 0 && d <= 12; }).slice(0, 4);
  const errN = feedErrors(srcAll);
  // người chỉ đọc tin (noperm) không thấy tab quản lý nguồn
  const readOnly = access.force === 'noperm';
  const cur: Tab = readOnly && tab === 'src' ? 'news' : tab;

  const failed = news.error ?? adv.error ?? idx.error ?? src.error;
  let state: ScreenState = 'ready';
  if (access.force && access.force !== 'noperm') state = access.force;
  else if (news.isPending || adv.isPending || idx.isPending || src.isPending) state = 'loading';
  else if (failed) state = failed instanceof DataError && failed.code === 'noperm' ? 'noperm' : 'error';
  else if (!newsAll.length && !advAll.length) state = 'empty';

  const tabs = [
    { key: 'news' as const, label: t('mn.tNews'), n: newsAll.filter((n) => n.relevance >= RELEVANT_MIN).length || undefined },
    { key: 'adv' as const, label: t('mn.carrierNotice'), n: advAll.length || undefined },
    { key: 'idx' as const, label: t('mn.tIdx') },
    ...(readOnly ? [] : [{ key: 'src' as const, label: errN ? t('mn.feedErr', { n: errN }) : t('mn.feed') }]),
  ];

  const [srcDw, setSrcDw] = useState<{ r: FeedSource | null } | null>(null);
  const wide = useWide();
  const [dockOpen, setDockOpen] = useState<boolean | null>(null);
  const dock = dockOpen ?? wide;
  const refetchAll = () => {
    void news.refetch();
    void adv.refetch();
    void idx.refetch();
    void src.refetch();
  };
  const PRI: Record<Tab, { label: string; icon: typeof IconRefresh; go: () => void }> = {
    news: { label: t('mn.refreshNews'), icon: IconRefresh, go: refetchAll },
    adv: { label: t('mn.refreshNotice'), icon: IconRefresh, go: refetchAll },
    idx: { label: t('mn.refreshIdx'), icon: IconRefresh, go: refetchAll },
    src: { label: t('mn.addSource'), icon: IconPlus, go: () => setSrcDw({ r: null }) },
  };
  const pri = PRI[cur];
  const PriIcon = pri.icon;

  const showDock = dock && cur !== 'idx';
  const dockEl = (
          <div className={`${s.dock} ${wide ? '' : s.float}`}>
            {!wide && <Button className={s.dockX} variant="ghost" size="sm" aria-label={t('mn.close')} icon={<IconX size={16} aria-hidden="true" />} onClick={() => setDockOpen(false)} />}
            <b>{t('mn.idxWeek')}</b>
            {idxAll.map((i) => {
              const st = indexStat(i);
              const pct = `${st.delta < 0 ? '−' : '+'}${viNum(Math.abs(st.pct), 1)}%`;
              return (
                <div key={i.id} className={s.dockRow}>
                  <b>{i.id}</b>
                  <Spark values={i.values} label={i.id} width={96} h={24} tone={st.delta > 0 ? 'up' : undefined} />
                  <span className={s.dockVal}>{viNum(st.last)}</span>
                  <span className={st.delta > 0 ? s.dockUp : s.dockDown}>{pct}</span>
                </div>
              );
            })}
            <hr />
            <b>{t('mn.soon7')}</b>
            {soon.map((n) => (
              <button key={n.id} type="button" className={s.dockRow2} onClick={() => setTab('adv')}>
                <b>{n.carrier} · {n.titleVi}</b>
                <span>{t('mn.effOn', { v: dmy(n.effective) })}</span>
              </button>
            ))}
            {soon.length === 0 && <span className={s.sub}>{t('mn.none')}</span>}
          </div>
  );

  return (
    <>
      <Page kind="table" dock={showDock && wide && state === 'ready' ? dockEl : undefined}>
      <PageHeader
        title={t('mn.title')}
        tabs={<Segmented label={t('mn.section')} value={cur} onChange={setTab} items={tabs} />}
        actions={
          <>
            {cur !== 'idx' && <HeaderTool label={t('mn.tIdx')} icon={IconChartBar} active={dock} onClick={() => setDockOpen(!dock)} />}
            <HeaderTool label={t('mn.more')} icon={IconDots} />
            <Button variant="primary" icon={<PriIcon size={16} aria-hidden="true" />} onClick={pri.go}>{pri.label}</Button>
          </>
        }
      />
      <div className={s.page}>
        <div className={s.tabsPhone}>
          <Segmented label={t('mn.section')} value={cur} onChange={setTab} items={tabs} />
        </div>
        {readOnly && <div className={s.note} role="note">{t('mn.readOnly')}</div>}
        <StateView
          state={state}
          title={state === 'empty' ? t('mn.emptyT') : state === 'error' ? t('mn.errT') : state === 'noperm' ? t('mn.nopermT') : undefined}
          desc={state === 'error' ? t('mn.errD') : undefined}
          action={state === 'error' ? <Button onClick={refetchAll}>{t('mn.retry')}</Button> : undefined}
        >
          <div className={`${s.split} `}>
            <div className={s.col}>
              {cur === 'news' ? <NewsTab items={newsAll} read={new Set()} /> : cur === 'adv' ? <NoticeTab items={advAll} /> : cur === 'idx' ? <IndexTab items={idxAll} /> : <SourceTab items={srcAll} onEdit={(r) => setSrcDw({ r })} />}
            </div>
            {showDock && !wide && dockEl}
          </div>
        </StateView>
      </div>
      </Page>
      {srcDw && <FeedSourceDrawer key={srcDw.r?.id ?? 'new'} open source={srcDw.r} onClose={() => setSrcDw(null)} />}
    </>
  );
}

/** Khổ rộng (≥ 1800, như board `-1920`): cột Chỉ số cước mở sẵn và cố định; nhỏ hơn thì đóng sẵn, mở nổi. */
function useWide() {
  const q = '(min-width: 1800px)';
  const [w, setW] = useState(() => typeof window !== 'undefined' && window.matchMedia(q).matches);
  useEffect(() => {
    const m = window.matchMedia(q);
    const on = () => setW(m.matches);
    m.addEventListener('change', on);
    return () => m.removeEventListener('change', on);
  }, []);
  return w;
}
